import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  X, 
  Save, 
  Loader, 
  Edit3, 
  Settings,
  Sparkles,
  Calendar,
  Percent,
  DollarSign,
  Gift,
  Users,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { updateDiscountCode, DiscountCode } from '@/services/discountService';
import { AffiliateSelect } from '../AffiliateSelect';
import { Switch } from '../ui/switch';

interface EditDiscountCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  discount: DiscountCode | null;
  onUpdate: () => void;
}

export function EditDiscountCodeModal({ isOpen, onClose, discount, onUpdate }: EditDiscountCodeModalProps) {
  // Helper function to convert Date to YYYY-MM-DD format without timezone issues
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage' as 'percentage' | 'fixed' | 'free_shipping',
    value: 0,
    minOrderAmount: 0,
    maxUses: 0,
    status: 'active' as 'active' | 'inactive' | 'expired',
    startDate: '',
    endDate: '',
    affiliateId: undefined as string | undefined,
    membersOnly: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (discount) {
      setFormData({
        code: discount.code || '',
        description: discount.description || '',
        type: discount.type || 'percentage',
        value: discount.value || 0,
        minOrderAmount: discount.minOrderAmount || 0,
        maxUses: discount.maxUses || 0,
        status: discount.status || 'active',
        startDate: discount.startDate instanceof Date ? formatDateForInput(discount.startDate) : '',
        endDate: discount.endDate instanceof Date ? formatDateForInput(discount.endDate) : '',
        affiliateId: discount.affiliateId || undefined,
        membersOnly: !!discount.membersOnly
      });
      setErrors({});
    }
  }, [discount]);

  if (!isOpen || !discount) return null;

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Discount code is required';
    }

    if (formData.type !== 'free_shipping' && (!formData.value || formData.value <= 0)) {
      newErrors.value = 'Discount value must be greater than 0';
    }

    if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'Percentage cannot exceed 100%';
    }

    if (formData.maxUses < 0) {
      newErrors.maxUses = 'Maximum uses cannot be negative';
    }

    if (formData.minOrderAmount < 0) {
      newErrors.minOrderAmount = 'Minimum order amount cannot be negative';
    }

    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setFormData({
      code: '',
      description: '',
      type: 'percentage',
      value: 0,
      minOrderAmount: 0,
      maxUses: 0,
      status: 'active',
      startDate: '',
      endDate: '',
      affiliateId: undefined,
      membersOnly: false
    });
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm() || !discount) return;

    setIsSubmitting(true);
    try {
      const updatedData: any = {
        ...formData,
        startDate: new Date(formData.startDate + 'T00:00:00'),
        endDate: new Date(formData.endDate + 'T23:59:59')
      };
      
      // Only include affiliateId if it has a value
      if (!formData.affiliateId) {
        delete updatedData.affiliateId;
      }
      
      await updateDiscountCode(discount.id, updatedData);
      toast.success('Discount code updated successfully');
      onUpdate();
      handleClose();
    } catch (error) {
      console.error('Error updating discount code:', error);
      toast.error('Failed to update discount code');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
        style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 p-6 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="h-12 w-12 bg-white/20 rounded-xl backdrop-blur-sm flex items-center justify-center"
                >
                  <Edit3 className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-2xl font-bold text-white"
                  >
                    Edit Discount Code
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-white/80 text-sm"
                  >
                    Update the promotional discount code settings
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Content */}
          <div className="p-8 space-y-8 max-h-[calc(90vh-200px)] overflow-y-auto">
            {/* Basic Information */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                <div className="h-8 w-8 bg-gradient-to-r from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center">
                  <Settings className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Discount Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SUMMER30"
                    className={`h-12 rounded-xl border-gray-200 text-base font-mono ${errors.code ? 'border-red-300 bg-red-50' : ''}`}
                    maxLength={20}
                  />
                  {errors.code && <p className="text-xs text-red-500">{errors.code}</p>}
                  <p className="text-xs text-gray-500">Use uppercase letters and numbers only</p>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Discount Type</Label>
                  <div className="space-y-3">
                    {(['percentage', 'fixed', 'free_shipping'] as const).map((type) => {
                      const typeLabels = {
                        percentage: 'Percentage Off',
                        fixed: 'Fixed Amount Off',
                        free_shipping: 'Free Shipping'
                      };
                      const typeIcons = {
                        percentage: Percent,
                        fixed: DollarSign,
                        free_shipping: Gift
                      };
                      const Icon = typeIcons[type];

                      return (
                        <motion.div
                          key={type}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                            formData.type === type
                              ? 'border-jamaica-green bg-jamaica-green/5 shadow-lg shadow-jamaica-green/10'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                          onClick={() => setFormData({ 
                            ...formData, 
                            type, 
                            value: type === 'free_shipping' ? 0 : 10 
                          })}
                        >
                          <input
                            type="radio"
                            name="discountType"
                            value={type}
                            checked={formData.type === type}
                            onChange={() => {}}
                            className="absolute top-3 right-3 h-4 w-4 text-jamaica-green border-gray-300 focus:ring-jamaica-green"
                          />
                          <div className="flex items-center gap-3 pr-8">
                            <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                              formData.type === type 
                                ? 'bg-jamaica-green text-white' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{typeLabels[type]}</div>
                              <div className="text-xs text-gray-500">
                                {type === 'percentage' && 'Reduce price by percentage'}
                                {type === 'fixed' && 'Reduce price by fixed amount'}
                                {type === 'free_shipping' && 'Waive shipping costs'}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-900">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this discount code..."
                  className="rounded-xl border-gray-200 text-base min-h-[80px]"
                  maxLength={200}
                />
              </div>
            </motion.div>

            {/* Discount Configuration */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Discount Configuration</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {formData.type !== 'free_shipping' && (
                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-900">
                      {formData.type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                    </Label>
                    <Input
                      type="number"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                      placeholder={formData.type === 'percentage' ? '10' : '25.00'}
                      className={`h-12 rounded-xl border-gray-200 text-base ${errors.value ? 'border-red-300 bg-red-50' : ''}`}
                      min="0"
                      max={formData.type === 'percentage' ? 100 : undefined}
                      step={formData.type === 'percentage' ? 1 : 0.01}
                    />
                    {errors.value && <p className="text-xs text-red-500">{errors.value}</p>}
                  </div>
                )}

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Minimum Order Amount ($)</Label>
                  <Input
                    type="number"
                    value={formData.minOrderAmount}
                    onChange={(e) => setFormData({ ...formData, minOrderAmount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className={`h-12 rounded-xl border-gray-200 text-base ${errors.minOrderAmount ? 'border-red-300 bg-red-50' : ''}`}
                    min="0"
                    step="0.01"
                  />
                  {errors.minOrderAmount && <p className="text-xs text-red-500">{errors.minOrderAmount}</p>}
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Maximum Uses</Label>
                  <Input
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) => setFormData({ ...formData, maxUses: parseInt(e.target.value) || 0 })}
                    placeholder="100"
                    className={`h-12 rounded-xl border-gray-200 text-base ${errors.maxUses ? 'border-red-300 bg-red-50' : ''}`}
                    min="1"
                  />
                  {errors.maxUses && <p className="text-xs text-red-500">{errors.maxUses}</p>}
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Status</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {['active', 'inactive'].map((status) => (
                      <motion.button
                        key={status}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => setFormData({ ...formData, status: status as 'active' | 'inactive' })}
                        className={`p-3 rounded-xl border-2 transition-all duration-300 text-left ${
                          formData.status === status
                            ? 'border-jamaica-green bg-jamaica-green/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium capitalize">{status}</div>
                        <div className="text-xs text-gray-500">
                          {status === 'active' ? 'Available for use' : 'Temporarily disabled'}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="col-span-1 md:col-span-2 border border-amber-100 rounded-2xl p-4 flex items-start justify-between bg-amber-50/60">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center border border-amber-100">
                      <Crown className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900 block">Rush'd Members Only</Label>
                      <p className="text-sm text-gray-600">
                        Enable this to restrict the code to active JamPack'd Rush'd members only.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.membersOnly}
                    onCheckedChange={(checked) => setFormData({ ...formData, membersOnly: checked })}
                  />
                </div>
              </div>
            </motion.div>

            {/* Schedule */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                <div className="h-8 w-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Schedule</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="h-12 rounded-xl border-gray-200 text-base"
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-900">End Date</Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className={`h-12 rounded-xl border-gray-200 text-base ${errors.endDate ? 'border-red-300 bg-red-50' : ''}`}
                  />
                  {errors.endDate && <p className="text-xs text-red-500">{errors.endDate}</p>}
                </div>
              </div>
            </motion.div>

            {/* Affiliate Selection */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                <div className="h-8 w-8 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Affiliate</h3>
              </div>

                             <div className="space-y-3">
                 <Label className="text-base font-semibold text-gray-900">Link to Affiliate</Label>
                 <AffiliateSelect
                   value={formData.affiliateId}
                   onChange={(affiliateId) => setFormData({ ...formData, affiliateId })}
                   placeholder="Select affiliate (optional)"
                   className="h-12"
                 />
                 <p className="text-xs text-gray-500">
                   Link this discount code to an affiliate to track commission attribution. When users apply this code, the linked affiliate will earn commissions from successful orders.
                 </p>
               </div>
            </motion.div>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  variant="outline" 
                  onClick={handleClose} 
                  disabled={isSubmitting}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-jamaica-green/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Discount Code
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
} 
